import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SETTINGS_TAB_BY_NAME } from "./SettingsDialog";

/**
 * Handles `/settings` URLs used as Stripe Checkout return and Customer Portal return paths.
 * Forwards billing query params to `/home` (where they are shown), or opens Settings over Home.
 * `?tab=<name>` or `?section=<name>` (ai, pricing, integrations, email, autopilot) opens that tab,
 * for email links such as "Turn off meeting recap emails" (`/settings?section=autopilot`).
 * @returns {JSX.Element} Minimal loading UI while redirecting.
 */
function StripeBillingReturnRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const billing = searchParams.get("billing");
    const sessionId = searchParams.get("session_id");

    if (billing === "success" || billing === "canceled") {
      const next = new URLSearchParams();
      next.set("billing", billing);
      if (sessionId) next.set("session_id", sessionId);
      navigate(`/home?${next.toString()}`, { replace: true });
      return;
    }

    const tabName = String(searchParams.get("tab") || searchParams.get("section") || "").toLowerCase();
    const initialTab = Object.prototype.hasOwnProperty.call(SETTINGS_TAB_BY_NAME, tabName)
      ? SETTINGS_TAB_BY_NAME[tabName]
      : 0;
    navigate("/home", { replace: true });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("quillai:open-settings", { detail: { initialTab } }));
    }, 0);
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "40vh",
        gap: 2,
      }}
    >
      <CircularProgress size={40} />
      <Typography color="text.secondary">Redirecting…</Typography>
    </Box>
  );
}

export default StripeBillingReturnRedirect;
